import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import Header from "./components/Header";
import FilterChips from "./components/FilterChips";
import MapViewGoogle from "./components/MapView";
import MapViewLeaflet from "./components/MapViewLeaflet";
// Map engine toggle — set REACT_APP_MAP_ENGINE=leaflet in frontend/.env to switch.
// Defaults to Google Maps so production is unaffected until you flip the flag.
const MapView =
    (process.env.REACT_APP_MAP_ENGINE || "").toLowerCase() === "leaflet"
        ? MapViewLeaflet
        : MapViewGoogle;
import JobList from "./components/JobList";
import ApplyModal from "./components/ApplyModal";
import LeadsAdmin from "./components/LeadsAdmin";
import PrivacyPage from "./components/PrivacyPage";
import MyApplicationsPage from "./components/MyApplicationsPage";
import ShareMenu from "./components/ShareMenu";
import HelpButton from "./components/HelpButton";
import EmployerPortal from "./components/EmployerPortal";
import JobAlertsPage from "./components/JobAlertsPage";
import SavedJobsPage from "./components/SavedJobsPage";
import OnboardingCards, { hasCompletedOnboarding } from "./components/OnboardingCards";
import ProductTutorial, { hasSeenTutorial } from "./components/ProductTutorial";
import { Share2, Bell } from "lucide-react";
import {
    fetchJobs, detectCountry, setCountry, normalize,
    hasSeenOnboarding, markOnboardingSeen, geocodeSearch,
} from "./lib/api";
import { STARTER_JOBS, loadJobsCache, saveJobsCache } from "./lib/starterJobs";
import { useLang } from "./lib/i18n";

function App() {
    const { t } = useLang();
    // Show something instantly ONLY if we have a real cached snapshot from a previous visit
    // (100+ jobs, <30 min old — enforced inside loadJobsCache). We NEVER seed with the tiny
    // 35-item bundled starter set anymore, because on a fresh browser that misled users into
    // thinking Dailyworkjobs only had 35 jobs. Empty array + loading overlay is the honest UX.
    const [jobs, setJobs] = useState(() => loadJobsCache() || []);
    const [loading, setLoading] = useState(() => (loadJobsCache() || []).length === 0);
    const [activeCat, setActiveCat] = useState("all");
    const [visibleBounds, setVisibleBounds] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [center, setCenter] = useState(null);
    const [zoom, setZoom] = useState(4);
    const [toast, setToastText] = useState("");
    const [route, setRoute] = useState(window.location.hash);
    const [focusJobId, setFocusJobId] = useState(null);
    const [showShare, setShowShare] = useState(false);
    // Onboarding: gate on first visit (skip for deep links so SEO/apply flows aren't interrupted).
    const [showOnboarding, setShowOnboarding] = useState(() => {
        try {
            const hasDeepLinkParams = /(?:^|[?&])(city|lat|cat)=/.test(window.location.search || "");
            const isDeepLinkHash = /#job=|#\/(alerts|saved|my|employer|privacy|leads)/.test(window.location.hash || "");
            if (hasDeepLinkParams || isDeepLinkHash) return false;
            return !hasCompletedOnboarding();
        } catch {
            return false;
        }
    });
    // Product tutorial: shows once, AFTER onboarding closes. Also shows for
    // returning visitors who saw the old onboarding but never got the tour.
    const [showTutorial, setShowTutorial] = useState(false);
    const lKeyPressCount = useRef(0);
    const lKeyResetTimer = useRef(null);
    // Mobile detection — used to enable per-viewport bbox fetching on small screens
    // so mobile users only download the pins currently on-screen (instead of the
    // full 1500+ NA payload). Desktop keeps the full-list behaviour.
    const [isMobile, setIsMobile] = useState(() => {
        try { return window.matchMedia("(max-width: 768px)").matches; } catch { return false; }
    });
    useEffect(() => {
        let mql;
        try {
            mql = window.matchMedia("(max-width: 768px)");
            const handler = (e) => setIsMobile(e.matches);
            if (mql.addEventListener) mql.addEventListener("change", handler);
            else mql.addListener(handler);
            return () => {
                if (mql.removeEventListener) mql.removeEventListener("change", handler);
                else mql.removeListener(handler);
            };
        } catch { /* ignore */ }
    }, []);
    const bboxRefetchTimer = useRef(null);
    const lastBboxKey = useRef("");

    const onToast = (msg) => {
        setToastText(msg);
        setTimeout(() => setToastText(""), 3200);
    };

    // Load real jobs (may be starting from empty — loading overlay shown until they arrive).
    useEffect(() => {
        (async () => {
            try {
                const list = await fetchJobs();
                if (list && list.length > 0) {
                    setJobs(list);
                    saveJobsCache(list);
                } else {
                    // API succeeded but returned nothing — fall back to bundled starter jobs
                    // so the map isn't empty. Rare (would need all providers down).
                    if (jobs.length === 0) setJobs(STARTER_JOBS);
                }
            } catch (e) {
                // Network / API failure: use bundled starter jobs as last-resort seed.
                if (jobs.length === 0) setJobs(STARTER_JOBS);
                onToast(t("loading.retry"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // SEO landing: honor ?city=<name>&cat=<id>&lat=<>&lng=<> query params — auto-filter + fly.
    useEffect(() => {
        if (jobs.length === 0) return;
        (async () => {
            try {
                const params = new URLSearchParams(window.location.search || "");
                const qCity = params.get("city");
                const qCat = params.get("cat");
                const qLat = parseFloat(params.get("lat"));
                const qLng = parseFloat(params.get("lng"));
                if (!qCity && !qCat && Number.isNaN(qLat)) return;

                if (qCat) setActiveCat(qCat);

                let lat = null;
                let lng = null;
                if (!Number.isNaN(qLat) && !Number.isNaN(qLng)) {
                    lat = qLat;
                    lng = qLng;
                } else if (qCity) {
                    try {
                        const geo = await geocodeSearch(qCity.trim());
                        if (geo && geo.length > 0) {
                            lat = geo[0].lat;
                            lng = geo[0].lng;
                        }
                    } catch { /* ignore */ }
                    if (lat == null) {
                        const nv = normalize(qCity);
                        const j = jobs.find((x) => normalize(x.city) === nv);
                        if (j && j.lat != null) {
                            lat = j.lat;
                            lng = j.lng;
                        }
                    }
                }
                if (lat != null && lng != null) {
                    setCenter([lat, lng]);
                    setZoom(10);
                    onToast(`Showing ${qCat ? qCat + " " : ""}jobs${qCity ? " in " + qCity : ""}`);
                }
                window.history.replaceState(null, "", window.location.pathname + window.location.hash);
            } catch {
                /* ignore */
            }
        })();
    }, [jobs]);

    // Initial center based on country (null = let map fit to continental NA on first load).
    useEffect(() => {
        const cc = detectCountry();
        setCountry(cc);
        // Don't force an initial center/zoom — MapView will fit to continental North America.
    }, []);

    // Onboarding hint
    useEffect(() => {
        // Skip onboarding tip when arriving via an SEO deep link — that toast is more useful.
        const hasDeepLinkParams = /(?:^|[?&])(city|lat|cat)=/.test(window.location.search || "");
        if (hasDeepLinkParams) return;
        if (!hasSeenOnboarding()) {
            setTimeout(() => {
                onToast(t("loading.tip"));
                markOnboardingSeen();
            }, 900);
        }
        // Product tutorial for returning users who never saw the new tour but
        // already completed the onboarding cards. Shown only on the main map
        // route (skip when the user landed inside a modal/route via deep-link).
        const isDeepLinkHash = /#job=|#\/(alerts|saved|my|employer|privacy|leads)/.test(window.location.hash || "");
        if (!hasDeepLinkParams && !isDeepLinkHash && !showOnboarding && !hasSeenTutorial()) {
            setTimeout(() => setShowTutorial(true), 1400);
        }
    }, []);

    // Route handling
    useEffect(() => {
        const onHash = () => {
            const h = window.location.hash;
            setRoute(h);
            const m = h.match(/#job=([^&]+)/);
            if (m) {
                setFocusJobId(decodeURIComponent(m[1]));
            }
        };
        window.addEventListener("hashchange", onHash);
        onHash();
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    // When focus job resolves in list, open its modal
    useEffect(() => {
        if (!focusJobId || jobs.length === 0) return;
        const j = jobs.find((x) => x.id === focusJobId);
        if (j) {
            // give map time to fly to it before opening modal
            setTimeout(() => setSelectedJob(j), 1400);
            setFocusJobId(null);
        }
    }, [focusJobId, jobs]);

    // Secret admin: press L three times
    useEffect(() => {
        const onKey = (e) => {
            if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.key && e.key.toLowerCase() === "l") {
                lKeyPressCount.current += 1;
                clearTimeout(lKeyResetTimer.current);
                lKeyResetTimer.current = setTimeout(() => {
                    lKeyPressCount.current = 0;
                }, 800);
                if (lKeyPressCount.current >= 3) {
                    lKeyPressCount.current = 0;
                    window.location.hash = "#leads";
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Mobile bbox refetch — only downloads the pins currently visible on the map.
    // Debounced so panning doesn't hammer the API. Desktop keeps the full-list
    // strategy (already lightweight from the in-memory cache and fine on wifi).
    useEffect(() => {
        if (!isMobile) return;
        if (!visibleBounds || !visibleBounds.bbox) return;
        const { south, west, north, east } = visibleBounds.bbox;
        // Skip the initial "world" bbox — we want the first fetch to be the wide
        // NA payload from the primary loader, then narrow as the user pans.
        if (!Number.isFinite(south) || !Number.isFinite(east)) return;
        // Round to 3 decimals (~110m) so tiny drift doesn't trigger a refetch.
        const key = `${south.toFixed(3)},${west.toFixed(3)},${north.toFixed(3)},${east.toFixed(3)}`;
        if (key === lastBboxKey.current) return;
        lastBboxKey.current = key;
        clearTimeout(bboxRefetchTimer.current);
        bboxRefetchTimer.current = setTimeout(async () => {
            try {
                const list = await fetchJobs({ bbox: key, limit: 800 });
                if (list && list.length > 0) {
                    setJobs(list);
                    saveJobsCache(list);
                }
            } catch (e) {
                // Silent — user still has the previous set of pins on the map.
                console.warn("[dwj] mobile bbox refetch failed:", e?.message || e);
            }
        }, 650);
        return () => clearTimeout(bboxRefetchTimer.current);
    }, [isMobile, visibleBounds]);

    // Filter + visible bounds
    const filteredAll = useMemo(() => {
        if (activeCat === "all") return jobs;
        return jobs.filter((j) => j.cat === activeCat);
    }, [jobs, activeCat]);

    const jobsInView = useMemo(() => {
        if (!visibleBounds) return filteredAll.slice(0, 200);
        const list = [];
        for (const j of filteredAll) {
            if (j.lat == null || j.lng == null) continue;
            if (visibleBounds.contains([j.lat, j.lng])) list.push(j);
        }
        // Sort by posted (most recent-ish). Fallback: keep order.
        return list;
    }, [filteredAll, visibleBounds]);

    // Category counts
    const counts = useMemo(() => {
        const m = {};
        for (const j of jobs) m[j.cat] = (m[j.cat] || 0) + 1;
        return m;
    }, [jobs]);

    // Search action
    const handleSearch = (sug) => {
        if (sug.type === "place") {
            // Geocode result — fly to lat/lng
            setCenter([sug.target.lat, sug.target.lng]);
            setZoom(11);
            onToast(`Showing jobs near ${sug.value}`);
            return;
        }
        if (sug.type === "city") {
            const nv = normalize(sug.value);
            const j = jobs.find((x) => normalize(x.city) === nv);
            if (j && j.lat != null) {
                setCenter([j.lat, j.lng]);
                setZoom(11);
                onToast(`Showing jobs in ${sug.value}`);
            } else if (sug.target?.lat != null) {
                setCenter([sug.target.lat, sug.target.lng]);
                setZoom(11);
            }
        } else {
            const j = sug.target;
            if (j.lat != null) {
                setCenter([j.lat, j.lng]);
                setZoom(12);
                setTimeout(() => setSelectedJob(j), 500);
            }
        }
    };

    const handleLocate = () => {
        if (!navigator.geolocation) {
            onToast("Geolocation not available on this device.");
            return;
        }
        onToast("Finding your location…");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                // enforce north america bounds
                if (
                    latitude >= 5 && latitude <= 75 &&
                    longitude >= -170 && longitude <= -50
                ) {
                    setCenter([latitude, longitude]);
                    setZoom(11);
                    onToast("Showing jobs near you 📍");
                } else {
                    onToast("Sorry, we only cover the USA & Canada right now.");
                }
            },
            () => onToast("Couldn’t get your location. Try search instead."),
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
        );
    };

    const isLeads = route.startsWith("#leads");
    const isPrivacy = route.startsWith("#/privacy") || route === "#privacy";
    const isMyApps = route.startsWith("#/my") || route === "#my";
    const isEmployer = route.startsWith("#/employer") || route === "#employer";
    const isAlerts = route.startsWith("#/alerts") || route === "#alerts";
    const isSaved = route.startsWith("#/saved") || route === "#saved";

    const shareUrl = window.location.origin + window.location.pathname;
    const shareText = "Dailyworkjobs — daily-wage hourly jobs across USA & Canada, one tap away.";

    return (
        <div className="App relative" style={{ height: "100vh", overflow: "hidden" }}>
            <MapView
                jobs={filteredAll}
                center={center}
                zoom={zoom}
                onJobClick={(j) => setSelectedJob(j)}
                onBoundsChange={setVisibleBounds}
                focusJobId={focusJobId}
            />

            {/* Initial job-load overlay — shown only until the first fetch resolves. */}
            {loading && jobs.length === 0 && (
                <div
                    className="fixed inset-0 z-[360] flex items-center justify-center pointer-events-none"
                    data-testid="jobs-loading-overlay"
                >
                    <div className="glass rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3 pointer-events-auto">
                        <div className="w-5 h-5 rounded-full border-2 border-[#e85812] border-t-transparent animate-spin" />
                        <div>
                            <div className="font-display font-semibold text-[14px] text-[#1a1e2c]">
                                {t("loading.jobs")}
                            </div>
                            <div className="text-[12px] text-slate-500">
                                {t("loading.subline")}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Header
                jobs={jobs}
                onSearch={handleSearch}
                onLocateMe={handleLocate}
                onOpenPrivacy={() => (window.location.hash = "#/privacy")}
            />

            <FilterChips
                counts={counts}
                active={activeCat}
                onChange={setActiveCat}
                total={jobs.length}
            />

            <JobList jobs={jobsInView} onJobClick={(j) => setSelectedJob(j)} />

            {/* Floating share button (site-wide) */}
            <div className="fixed right-4 md:right-6 z-[390]" style={{ bottom: "88px" }}>
                <div className="relative">
                    <button
                        onClick={() => setShowShare((s) => !s)}
                        className="w-12 h-12 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                        data-testid="site-share-btn"
                        aria-label="share dailyworkjobs"
                    >
                        <Share2 className="w-5 h-5 text-[#e85812]" />
                    </button>
                    {showShare && (
                        <ShareMenu
                            url={shareUrl}
                            text={shareText}
                            onClose={() => setShowShare(false)}
                            onToast={onToast}
                        />
                    )}
                </div>
            </div>

            {/* Floating job-alerts button */}
            <button
                onClick={() => (window.location.hash = "#/alerts")}
                className="fixed right-4 md:right-6 z-[390] w-12 h-12 rounded-full bg-[#e85812] shadow-xl flex items-center justify-center hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                style={{ bottom: "216px" }}
                data-testid="site-alerts-btn"
                aria-label="job alerts"
                title="Get daily job alerts"
            >
                <Bell className="w-5 h-5 text-white" />
            </button>

            {selectedJob && (
                <ApplyModal
                    job={selectedJob}
                    onClose={() => setSelectedJob(null)}
                    onToast={onToast}
                />
            )}

            {isLeads && (
                <LeadsAdmin
                    onClose={() => {
                        window.location.hash = "";
                    }}
                />
            )}

            {isPrivacy && (
                <PrivacyPage
                    onClose={() => {
                        window.location.hash = "";
                    }}
                />
            )}

            {isMyApps && (
                <MyApplicationsPage
                    onClose={() => {
                        window.location.hash = "";
                    }}
                />
            )}

            {isEmployer && (
                <EmployerPortal
                    onClose={() => {
                        window.location.hash = "";
                    }}
                    onToast={onToast}
                />
            )}

            {isAlerts && (
                <JobAlertsPage
                    onClose={() => {
                        window.location.hash = "";
                    }}
                    onToast={onToast}
                />
            )}

            {isSaved && (
                <SavedJobsPage
                    onClose={() => { window.location.hash = ""; }}
                    onToast={onToast}
                    onOpenJob={(r) => {
                        window.location.hash = "";
                        // Try to open apply modal for the real job in cache; otherwise open external URL.
                        const j = jobs.find((x) => x.id === r.job_id);
                        if (j) setSelectedJob(j);
                        else if (r.url) window.open(r.url, "_blank", "noopener");
                    }}
                />
            )}

            <HelpButton onToast={onToast} />

            {loading && (
                <div
                    className="fixed left-1/2 -translate-x-1/2 z-[300] pointer-events-none"
                    style={{ bottom: "92px" }}
                    data-testid="loading-pill"
                >
                    <div className="glass px-4 py-2 rounded-full shadow-lg flex items-center gap-2 font-body text-[13px] text-[#333A56]">
                        <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-[#ffdccb] border-t-[#e85812] animate-spin" />
                        {t("loading.pill")}
                    </div>
                </div>
            )}

            {toast && (
                <div className="snackbar animate-fade-up" data-testid="toast">
                    {toast}
                </div>
            )}

            {showOnboarding && (
                <OnboardingCards
                    onFinish={() => {
                        setShowOnboarding(false);
                        // Kick off product tutorial right after onboarding finishes.
                        if (!hasSeenTutorial()) {
                            setTimeout(() => setShowTutorial(true), 350);
                        }
                    }}
                />
            )}

            {showTutorial && (
                <ProductTutorial onFinish={() => setShowTutorial(false)} />
            )}
        </div>
    );
}

export default App;
